import { useState } from "react";
import AppLayout from "@/components/AppLayout";
import AlertPreferences from "@/components/AlertPreferences";
import TwoFactorSettings from "@/components/TwoFactorSettings";
import DriverProfileCard from "@/components/DriverProfileCard";
import { CrmCoverageBadges } from "@/components/EnterpriseCrm";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { inputCls, primaryBtn } from "@/components/AuthShell";
import { User, Mail, Shield, BellRing, Send, Stethoscope } from "lucide-react";

export default function Profile() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [smsHot, setSmsHot] = useState(user?.sms_hot_leads !== false);
  const [saving, setSaving] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [rnAuto, setRnAuto] = useState(user?.rn_auto_request === true);
  const isAttorney = user?.roles?.includes("attorney") || user?.roles?.includes("admin");
  const isPro = ["attorney", "admin", "affiliate", "pilot", "investigator"].some((r) => user?.roles?.includes(r));

  const testAlert = async () => {
    try { await api.post("/attorney/alert-preferences/test"); toast.success("Test alert sent — check your notifications bell."); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleRnAuto = async () => {
    const next = !rnAuto; setRnAuto(next);
    try {
      await api.put("/attorney/rn-settings", { enabled: next });
      setUser({ ...user, rn_auto_request: next });
      toast.success(next ? "Auto-Request RN Intake enabled" : "Auto-Request RN Intake disabled");
    } catch (e) { setRnAuto(!next); toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/auth/profile", { name, phone, sms_hot_leads: smsHot });
      setUser(data);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <AppLayout>
      <p className="text-xs font-medium text-slate-500 uppercase tracking-widest">Account</p>
      <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-navy mt-1">Profile & Settings</h1>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div className="bg-navy text-white rounded-3xl p-6 text-center">
            <div className="h-20 w-20 rounded-full bg-white/10 mx-auto flex items-center justify-center text-2xl font-heading font-bold">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="mt-4 font-heading font-bold text-lg">{user?.name}</div>
            <div className="text-slate-300 text-sm">{user?.email}</div>
            <span className="inline-flex items-center gap-1.5 mt-3 text-xs font-medium bg-white/10 px-3 py-1 rounded-full capitalize"><Shield className="w-3 h-3" /> {user?.role}</span>
          </div>
        </div>

        <div className="lg:col-span-2">
          <form onSubmit={save} className="bg-white rounded-3xl border border-slate-100 p-6 md:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] space-y-5">
            <div>
              <label className="text-sm font-medium text-slate-700 flex items-center gap-1.5"><User className="w-4 h-4" /> Full name</label>
              <input className={`${inputCls} mt-1.5`} value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 flex items-center gap-1.5"><Mail className="w-4 h-4" /> Email</label>
              <input className={`${inputCls} mt-1.5 opacity-60`} value={user?.email || ""} disabled />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Phone</label>
              <input className={`${inputCls} mt-1.5`} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+14155551234" data-testid="profile-phone" />
              {user?.roles?.includes("attorney") && (
                <p className="mt-1.5 text-xs text-slate-500" data-testid="profile-sms-hint">Add your mobile in international format (e.g. +14155551234) to get real-time <span className="font-medium text-cobalt">hot-lead SMS alerts</span> the moment a client is filling out or signing your retainer.</p>
              )}
            </div>
            {user?.roles?.includes("attorney") && (
              <div className="flex items-center justify-between rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3" data-testid="sms-toggle-row">
                <div>
                  <div className="text-sm font-medium text-slate-700">Hot-lead SMS alerts</div>
                  <div className="text-xs text-slate-500">Text me the instant a client is highly engaged or on the signature page.</div>
                </div>
                <button type="button" role="switch" aria-checked={smsHot} onClick={() => setSmsHot((v) => !v)} data-testid="profile-sms-toggle"
                  className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${smsHot ? "bg-cobalt" : "bg-slate-300"}`}>
                  <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${smsHot ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
              </div>
            )}
            <button type="submit" disabled={saving} className={`${primaryBtn} max-w-[180px]`} data-testid="profile-save">{saving ? "Saving…" : "Save changes"}</button>
          </form>

          {isAttorney && (
            <div className="mt-6 bg-white rounded-3xl border border-slate-100 p-6 md:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="profile-crm-coverage">
              <h3 className="font-heading font-bold text-navy">Enterprise CRM Compatible</h3>
              <p className="text-sm text-slate-500 mt-1 max-w-md">Cases you claim synchronize automatically into your connected CRM — contacts, matter, evidence and timeline, with no duplicate data entry.</p>
              <div className="mt-4"><CrmCoverageBadges attorneyId={user?.user_id} title="Your CRM coverage" emptyHint /></div>
            </div>
          )}
          {isAttorney && (
            <div className="mt-6 bg-white rounded-3xl border border-slate-100 p-6 md:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="profile-alert-prefs">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <h3 className="font-heading font-bold text-navy flex items-center gap-2"><BellRing className="w-5 h-5 text-cobalt" /> Marketplace Alert Preferences</h3>
                  <p className="text-sm text-slate-500 mt-1 max-w-md">Choose exactly which new-case alerts you receive — by score, state, source, commercial-only, and SMS. First to claim wins.</p>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button onClick={() => setShowPrefs(true)} data-testid="profile-open-prefs" className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-cobalt text-white text-sm font-semibold hover:bg-cobalt-hover"><BellRing className="w-4 h-4" /> Manage preferences</button>
                <button onClick={testAlert} data-testid="profile-test-alert" className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-slate-200 text-navy text-sm font-medium hover:border-cobalt hover:text-cobalt"><Send className="w-4 h-4" /> Send test alert</button>
              </div>
            </div>
          )}
          {isAttorney && (
            <div className="mt-6 bg-white rounded-3xl border border-slate-100 p-6 md:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="profile-rn-auto">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="max-w-md">
                  <h3 className="font-heading font-bold text-navy flex items-center gap-2"><Stethoscope className="w-5 h-5 text-cobalt" /> Auto-Request RN Intake</h3>
                  <p className="text-sm text-slate-500 mt-1">Automatically request a licensed nurse triage when you claim a qualifying case. Gold (RN-Vetted) cases bundle a <span className="font-semibold text-amber-600">$150 premium</span> into the claim price for completed clinical intake.</p>
                </div>
                <button type="button" role="switch" aria-checked={rnAuto} onClick={toggleRnAuto} data-testid="profile-rn-auto-toggle"
                  className={`w-12 h-6 rounded-full transition-colors relative shrink-0 ${rnAuto ? "bg-cobalt" : "bg-slate-300"}`}>
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${rnAuto ? "translate-x-6" : "translate-x-0.5"}`} />
                </button>
              </div>
            </div>
          )}
          {!isPro && <DriverProfileCard />}
        </div>
      </div>
      {showPrefs && <AlertPreferences onClose={() => setShowPrefs(false)} />}
    </AppLayout>
  );
}
